import { Segment } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Accordion, SpinningLoader, Text } from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { AddSegmentButton } from './add-segment-button';
import { FiltersList } from './filters-list';
import { SegmentItem } from './segment-item';
import { SegmentsViewFooter } from './segments-view-footer';
import { SegmentsViewHeader } from './segments-view-header';

type Props = {
  segments: Segment[];
};

export const SegmentsView = ({ segments }: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { deleteSegments, isUpsertingAudience, locationIds, setMode, setFilters } = useAudienceTray();

  const handleAddSegment = () => {
    setFilters({});
    setMode('filters');
  };

  if (segments.length) {
    return (
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: locationIds.length === 1 ? theme.spacing(4, 0, 0) : theme.spacing(4, 4, 0),
          rowGap: theme.spacing(2),
        }}
      >
        <Accordion css={{ width: '100%' }}>
          {segments.map((segment, index) => (
            <Accordion.Item key={segment.segmentId + '-' + index} value={segment.segmentId ?? ''}>
              <Accordion.Header>
                <SegmentItem
                  name={t('Segment {{index}}', { index: index + 1 })}
                  segment={segment}
                  onDelete={() => deleteSegments([segment])}
                />
              </Accordion.Header>
              <Accordion.Body css={{ display: 'flex', flexDirection: 'column', rowGap: theme.spacing(2) }}>
                <FiltersList filterOptions={segment.filterOptions} />
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
        {isUpsertingAudience && (
          <Text css={{ display: 'flex', alignItems: 'center', marginTop: theme.spacing(2) }}>
            <SpinningLoader size='xs' css={{ marginRight: theme.spacing(0.5) }} />
            {t('Creating Segment...')}
          </Text>
        )}
        <AddSegmentButton handleAddSegment={handleAddSegment} css={{ marginTop: theme.spacing(3) }} />
      </div>
    );
  }

  return (
    <>
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center',
          padding: theme.spacing(0, 4),
          rowGap: theme.spacing(2),
        }}
      >
        {isUpsertingAudience && (
          <Text
            color='subdued'
            weight='bold'
            css={{ display: 'flex', alignItems: 'center', marginTop: theme.spacing(2) }}
          >
            <SpinningLoader size='xs' css={{ marginRight: theme.spacing(0.5) }} />
            {t('Creating Segment...')}
          </Text>
        )}
        {!isUpsertingAudience && (
          <Text color='subdued' weight='bold'>
            {t('No Segments')}
          </Text>
        )}
        <Text color='subdued' css={{ textAlign: 'center' }}>
          {t('Target multiple types of people in your audience at once with segments.')}
        </Text>
        <AddSegmentButton handleAddSegment={handleAddSegment} />
      </div>
    </>
  );
};

SegmentsView.Footer = SegmentsViewFooter;
SegmentsView.Header = SegmentsViewHeader;
